import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import {
  ArrowLeft,
  Bookmark,
  CalendarHeart,
  Code,
  FolderKanban,
  GraduationCap,
  Handshake,
  HeartHandshake,
  Landmark,
  House,
  LayoutGrid,
  LifeBuoy,
  Lock,
  Megaphone,
  Palette,
  Plus,
  Search,
  Trash2,
  Upload,
  Users,
  X,
} from 'lucide-react';
import { TEMPLATE_CATEGORIES, TEMPLATE_CATEGORY_LABELS, type Board, type BoardTemplateSummary, type TemplateCategory } from '@kaba/shared';
import { api } from '../lib/api';
import { useMe } from '../lib/auth';
import { toast } from '../lib/toast';
import type { Target } from '../lib/structure';
import { BoardPreview, MiniPreview } from './newboard/BoardPreview';
import { ImportPanel, type ImportTab } from './newboard/ImportPanel';
import { fieldClass, openNewBoard, TargetFields, useBoardTarget } from './newboard/target';
import { Button, cx, IconButton, Sheet } from './ui/ui';

type Cat = 'alle' | TemplateCategory | 'eigene';
type View = { kind: 'browse' } | { kind: 'detail'; id: string } | { kind: 'import' };

const CAT_ICON: Record<TemplateCategory | 'eigene', ReactNode> = {
  projekte: <FolderKanban size={16} />,
  veranstaltungen: <CalendarHeart size={16} />,
  bildung: <GraduationCap size={16} />,
  marketing: <Megaphone size={16} />,
  design: <Palette size={16} />,
  verein: <HeartHandshake size={16} />,
  finanzen: <Landmark size={16} />,
  personal: <Users size={16} />,
  it: <Code size={16} />,
  service: <LifeBuoy size={16} />,
  vertrieb: <Handshake size={16} />,
  persoenlich: <House size={16} />,
  eigene: <Bookmark size={16} />,
};

const plural = (x: number, one: string, many: string) => `${x} ${x === 1 ? one : many}`;
const norm = (s: string) => s.toLocaleLowerCase('de-DE');

/**
 * Neues Board (0.29): breiter Dialog mit „Leer starten“, „Importieren“ und dem Vorlagenkatalog nach Bereichen.
 * Eine Vorlage öffnet zuerst ihre Detailseite (Vorschau, Erklärung, Merkmale) – angelegt wird erst dort.
 */
export function NewBoardDialog({ open, onClose, target, initialImport }: { open: boolean; onClose: () => void; target?: Target | null; initialImport?: boolean }) {
  const { data: templates, refetch } = useQuery({
    queryKey: ['templates'],
    queryFn: () => api.get<BoardTemplateSummary[]>('/api/templates'),
    enabled: open,
  });
  const [view, setView] = useState<View>({ kind: 'browse' });
  const [cat, setCat] = useState<Cat>('alle');
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState<ImportTab>('text');
  const mainRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (open) {
      setView(initialImport ? { kind: 'import' } : { kind: 'browse' });
      setCat('alle');
      setQuery('');
      setTab('text');
    }
  }, [open, initialImport]);
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0 });
  }, [view, cat]);

  const catalog = useMemo(() => (templates ?? []).filter((t) => t.id !== 'builtin:empty'), [templates]);
  const counts = useMemo(() => {
    const m = new Map<Cat, number>([['alle', catalog.length]]);
    for (const t of catalog) m.set(t.category, (m.get(t.category) ?? 0) + 1);
    return m;
  }, [catalog]);
  const q = norm(query.trim());
  const shown = useMemo(() => {
    if (q)
      return catalog.filter((t) => norm([t.name, t.summary, t.description, TEMPLATE_CATEGORY_LABELS[t.category], ...t.tags, ...t.columns].join(' ')).includes(q));
    // 0.86: empfohlener Einstieg zuerst (unter „Alle“ und in seinem Bereich)
    const list = cat === 'alle' ? catalog : catalog.filter((t) => t.category === cat);
    return [...list.filter((t) => t.recommended), ...list.filter((t) => !t.recommended)];
  }, [catalog, cat, q]);
  const detail = view.kind === 'detail' ? templates?.find((t) => t.id === view.id) : undefined;

  const go = (c: Cat) => {
    setCat(c);
    setQuery('');
    setView({ kind: 'browse' });
  };
  const navItem = (active: boolean) =>
    cx(
      'flex min-h-11 w-full items-center gap-2.5 rounded-lg px-2.5 text-left text-sm sm:min-h-9',
      active ? 'bg-accent-soft font-semibold' : 'hover:bg-black/[0.04] dark:hover:bg-white/5',
    );
  const cats: Cat[] = ['alle', ...TEMPLATE_CATEGORIES.map(([c]) => c), ...(counts.get('eigene') ? (['eigene'] as const) : [])];
  const catLabel = (c: Cat) => (c === 'alle' ? 'Alle Vorlagen' : TEMPLATE_CATEGORY_LABELS[c]);
  const heading = view.kind === 'import' ? 'Importieren' : view.kind === 'detail' ? (detail?.id === 'builtin:empty' ? 'Leer starten' : 'Vorlage') : 'Neues Board';

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()} title="Neues Board" side="wide" bare>
      <div className="pt-safe flex items-center gap-2 border-b border-[var(--line)] px-3 py-2 sm:px-4">
        {view.kind !== 'browse' && (
          <IconButton label="Zurück zur Übersicht" onClick={() => setView({ kind: 'browse' })}>
            <ArrowLeft size={20} />
          </IconButton>
        )}
        <h2 className="flex-1 truncate px-1 text-base font-semibold">{heading}</h2>
        <Dialog.Close asChild>
          <IconButton label="Schließen">
            <X size={20} />
          </IconButton>
        </Dialog.Close>
      </div>

      <div className="flex min-h-0 flex-1">
        {/* Seitenleiste (ab Tablet) */}
        <nav aria-label="Vorlagen und Wege" className="hidden w-64 shrink-0 flex-col gap-0.5 overflow-y-auto border-r border-[var(--line)] p-3 md:flex">
          <button type="button" className={navItem(view.kind === 'detail' && view.id === 'builtin:empty')} onClick={() => setView({ kind: 'detail', id: 'builtin:empty' })}>
            <Plus size={16} /> Leer starten
          </button>
          <button type="button" className={navItem(view.kind === 'import')} onClick={() => setView({ kind: 'import' })}>
            <Upload size={16} /> Importieren
          </button>
          <p className="mt-4 mb-1 px-2.5 text-[11px] font-semibold tracking-wider text-[var(--ink-soft)] uppercase">Vorlagen</p>
          {cats.map((c) => (
            <button
              key={c}
              type="button"
              aria-current={view.kind === 'browse' && !q && cat === c ? 'page' : undefined}
              className={navItem(view.kind === 'browse' && !q && cat === c)}
              onClick={() => go(c)}
            >
              {c === 'alle' ? <LayoutGrid size={16} /> : CAT_ICON[c]}
              <span className="min-w-0 flex-1 truncate">{catLabel(c)}</span>
              <span className="text-xs text-[var(--ink-soft)]">{counts.get(c) ?? 0}</span>
            </button>
          ))}
        </nav>

        <div ref={mainRef} className="pb-safe min-w-0 flex-1 overflow-y-auto">
          {view.kind === 'browse' && (
            <div className="flex flex-col gap-4 p-4 sm:p-5">
              {/* Handy: Wege und Bereiche oben */}
              <div className="grid grid-cols-2 gap-2 md:hidden">
                <Button onClick={() => setView({ kind: 'detail', id: 'builtin:empty' })}>
                  <Plus size={16} /> Leer starten
                </Button>
                <Button onClick={() => setView({ kind: 'import' })}>
                  <Upload size={16} /> Importieren
                </Button>
              </div>
              <div className="relative">
                <Search size={16} className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-[var(--ink-soft)]" aria-hidden />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Vorlagen durchsuchen …"
                  aria-label="Vorlagen durchsuchen"
                  className={cx(fieldClass, 'pl-9')}
                />
              </div>
              <div className="-mx-4 flex gap-1.5 overflow-x-auto px-4 pb-1 md:hidden" role="group" aria-label="Bereiche">
                {cats.map((c) => (
                  <button
                    key={c}
                    type="button"
                    aria-pressed={!q && cat === c}
                    onClick={() => go(c)}
                    className={cx(
                      'inline-flex min-h-10 shrink-0 items-center gap-1.5 rounded-full border px-3 text-sm',
                      !q && cat === c ? 'border-accent bg-accent-soft font-medium' : 'border-[var(--line)]',
                    )}
                  >
                    {catLabel(c)} <span className="text-xs text-[var(--ink-soft)]">{counts.get(c) ?? 0}</span>
                  </button>
                ))}
              </div>
              <h3 className="text-lg font-semibold" aria-live="polite">
                {q ? `${plural(shown.length, 'Treffer', 'Treffer')} für „${query.trim()}“` : catLabel(cat)}
              </h3>
              {!templates ? (
                <p className="text-sm text-[var(--ink-soft)]">Vorlagen werden geladen …</p>
              ) : shown.length ? (
                <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {shown.map((t) => (
                    <li key={t.id}>
                      <TemplateTile t={t} onOpen={() => setView({ kind: 'detail', id: t.id })} />
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-[var(--ink-soft)]">
                  Keine passende Vorlage. Du kannst <button type="button" className="text-accent-fg underline" onClick={() => setView({ kind: 'detail', id: 'builtin:empty' })}>leer starten</button>{' '}
                  oder deine Aufgaben <button type="button" className="text-accent-fg underline" onClick={() => setView({ kind: 'import' })}>als Text einfügen</button>.
                </p>
              )}
            </div>
          )}

          {view.kind === 'detail' && detail && (
            <TemplateDetail
              key={detail.id}
              t={detail}
              open={open}
              target={target}
              onClose={onClose}
              onDeleted={() => {
                setView({ kind: 'browse' });
                void refetch();
              }}
            />
          )}
          {view.kind === 'detail' && !detail && <p className="p-5 text-sm text-[var(--ink-soft)]">Vorlage wird geladen …</p>}

          {view.kind === 'import' && (
            <div className="p-4 sm:p-5">
              <ImportPanel open={open} tab={tab} onTab={setTab} target={target} onClose={onClose} />
            </div>
          )}
        </div>
      </div>
    </Sheet>
  );
}

function TemplateTile({ t, onOpen }: { t: BoardTemplateSummary; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group flex h-full w-full flex-col overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--panel)] text-left transition hover:border-accent hover:shadow-md focus-visible:border-accent"
    >
      <MiniPreview preview={t.preview} color={t.color} />
      <span className="flex flex-1 flex-col gap-1 p-3">
        <span className="flex items-center gap-1.5 font-semibold">
          <span className="min-w-0 truncate">{t.name}</span>
          {!t.builtIn && <span className="shrink-0 rounded bg-black/5 px-1.5 text-[11px] font-normal dark:bg-white/10">{t.shared ? 'Für alle' : 'Eigene'}</span>}
          {t.recommended && <span className="shrink-0 rounded bg-accent-soft px-1.5 text-[11px] font-medium">Empfohlen zum Start</span>}
          {t.governed.length > 0 && (
            <span className="inline-flex shrink-0 items-center gap-0.5 rounded bg-black/5 px-1.5 text-[11px] font-normal dark:bg-white/10" title="Mit verbindlichen Regeln (Vier-Augen-Prinzip, Freigaben)">
              <Lock size={11} aria-hidden /> Geregelt
            </span>
          )}
        </span>
        <span className="line-clamp-2 text-sm text-[var(--ink-soft)]">{t.summary}</span>
        <span className="mt-auto pt-1 text-xs text-[var(--ink-soft)]">
          {plural(t.columns.length, 'Spalte', 'Spalten')}
          {t.automationCount > 0 && ` · ${plural(t.automationCount, 'Automation', 'Automationen')}`}
        </span>
      </span>
    </button>
  );
}

function TemplateDetail({ t, open, target, onClose, onDeleted }: { t: BoardTemplateSummary; open: boolean; target?: Target | null; onClose: () => void; onDeleted: () => void }) {
  const { data: me } = useMe();
  const tgt = useBoardTarget(open, target);
  const [mode, setMode] = useState<'board' | 'list'>('board');
  const [title, setTitle] = useState('');
  const [withCards, setWithCards] = useState(t.cardsDefault);
  const [setupDate, setSetupDate] = useState('');
  const [busy, setBusy] = useState(false);
  const empty = t.id === 'builtin:empty';
  const ruleCols = t.preview.columns.filter((c) => c.wipLimit != null || c.entryRule || c.exitRule).length;
  const brings: [string, string][] = [
    ...(t.preview.labels.length ? [['Labels', t.preview.labels.map((l) => l.name).join(', ')] as [string, string]] : []),
    ...(ruleCols ? [['Spaltenregeln', `${plural(ruleCols, 'Spalte', 'Spalten')} mit Regel oder Begrenzung`] as [string, string]] : []),
    ...(t.preview.fields.length ? [['Eigene Felder', t.preview.fields.join(', ')] as [string, string]] : []),
    ...(t.preview.cardTemplates.length ? [['Kartenvorlagen', t.preview.cardTemplates.join(', ')] as [string, string]] : []),
    ...(t.preview.views.length ? [['Gespeicherte Ansichten', t.preview.views.join(', ')] as [string, string]] : []),
    ...(t.preview.overview.length ? [['Boardübersicht', t.preview.overview.join(', ')] as [string, string]] : []),
    ...(t.preview.forms.length ? [['Formularentwurf', `${t.preview.forms.join(', ')} (unveröffentlicht – du entscheidest, ob und wann es online geht)`] as [string, string]] : []),
  ];

  return (
    <div className="grid gap-5 p-4 sm:p-5 lg:grid-cols-[minmax(0,1fr)_19rem] lg:grid-rows-[auto_1fr]">
      {/* Erklärung */}
      <div className="flex flex-col gap-3 lg:col-start-2 lg:row-start-1">
        <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-black/[0.04] px-2.5 py-0.5 text-xs dark:bg-white/10">
          {CAT_ICON[t.category]} {empty ? 'Ohne Vorlage' : TEMPLATE_CATEGORY_LABELS[t.category]}
        </span>
        <h3 className="text-xl font-semibold">{t.name}</h3>
        <p className="text-sm leading-relaxed">{t.description}</p>
        <dl className="grid grid-cols-3 gap-2">
          <Stat label="Spalten" value={t.columns.length} />
          <Stat label="Beispielkarten" value={t.cardCount} />
          <Stat label="Automationen" value={t.automationCount} />
        </dl>
        {t.tags.length > 0 && (
          <ul className="flex flex-wrap gap-1.5" aria-label="Merkmale">
            {t.tags.map((x) => (
              <li key={x} className="rounded-full px-2.5 py-0.5 text-xs" style={{ background: `color-mix(in oklab, ${t.color ?? 'var(--color-accent)'} 16%, transparent)` }}>
                {x}
              </li>
            ))}
          </ul>
        )}
        {t.recommended && (
          <p className="rounded-lg bg-accent-soft p-3 text-sm">Empfohlen zum Start: keine Pflichtschritte, keine Felder – Spalten, Labels und Regeln kannst du später jederzeit ergänzen.</p>
        )}
        {/* 0.86: geregelter Ablauf – wer beteiligt sein muss, was gesperrt wird, wonach gefragt wird */}
        {t.governed.length > 0 && (
          <div className="flex flex-col gap-1.5 rounded-lg border border-[var(--line)] p-3 text-sm">
            <h4 className="inline-flex items-center gap-1.5 font-semibold">
              <Lock size={14} aria-hidden /> Geregelter Ablauf
            </h4>
            <ul className="list-disc pl-5 text-[var(--ink-soft)]">
              {t.governed.map((x, i) => (
                <li key={i}>{x}</li>
              ))}
            </ul>
          </div>
        )}
        {t.adjust.length > 0 && (
          <div className="flex flex-col gap-1.5 text-sm">
            <h4 className="font-semibold">Vor dem Start prüfen und anpassen</h4>
            <ul className="list-disc pl-5 text-[var(--ink-soft)]">
              {t.adjust.map((x, i) => (
                <li key={i}>{x}</li>
              ))}
            </ul>
            <p className="text-xs text-[var(--ink-soft)]">Das sind Vorschläge der Vorlage – nach dem Anlegen in den Feldern, Spaltenregeln und Automationen änderbar.</p>
          </div>
        )}
        {(brings.length > 0 || t.preview.automations.length > 0) && (
          <div className="flex flex-col gap-1.5 text-sm">
            <h4 className="font-semibold">Was die Vorlage mitbringt</h4>
            <dl className="flex flex-col gap-1">
              {brings.map(([k, v]) => (
                <div key={k}>
                  <dt className="inline font-medium">{k}: </dt>
                  <dd className="inline text-[var(--ink-soft)]">{v}</dd>
                </div>
              ))}
            </dl>
            {t.preview.automations.length > 0 && (
              <>
                <p className="font-medium">Automationen:</p>
                <ul className="list-disc pl-5 text-[var(--ink-soft)]">
                  {t.preview.automations.map((a, i) => (
                    <li key={i}>{a}</li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </div>

      {/* Vorschau */}
      <section aria-label="Vorschau" className="flex min-w-0 flex-col gap-2 lg:col-start-1 lg:row-span-2 lg:row-start-1">
        <div className="flex items-center gap-2">
          <h4 className="flex-1 text-sm font-semibold">Vorschau</h4>
          <div role="group" aria-label="Darstellung der Vorschau" className="inline-flex rounded-lg bg-black/[0.04] p-0.5 dark:bg-white/5">
            {(['board', 'list'] as const).map((m) => (
              <button
                key={m}
                type="button"
                aria-pressed={mode === m}
                onClick={() => setMode(m)}
                className={cx('min-h-9 rounded-md px-3 text-sm', mode === m ? 'bg-[var(--panel)] font-medium shadow-sm' : 'text-[var(--ink-soft)]')}
              >
                {m === 'board' ? 'Board' : 'Liste'}
              </button>
            ))}
          </div>
        </div>
        <div className="rounded-xl p-3" style={{ background: `color-mix(in oklab, ${t.color ?? 'var(--color-accent)'} 9%, var(--panel))` }}>
          <BoardPreview preview={t.preview} mode={mode} empty={empty ? 'Leere Spalten – die Karten legst du selbst an.' : 'Diese Vorlage hat keine Beispielkarten.'} />
        </div>
      </section>

      {/* Anlegen */}
      <form
        className="flex flex-col gap-3 border-t border-[var(--line)] pt-4 lg:col-start-2 lg:row-start-2 lg:border-t-0 lg:pt-0"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!title.trim()) return;
          setBusy(true);
          try {
            const cards = withCards && t.cardCount > 0;
            const b = await api.post<Board>('/api/boards', {
              title: title.trim(),
              templateId: t.id,
              withCards: cards,
              ...(cards && t.setup && setupDate ? { setupDate } : {}),
              ...tgt.body,
            });
            await openNewBoard(b.id, onClose);
          } catch (err) {
            toast.error((err as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        <label className="flex flex-col gap-1.5 text-sm font-medium">
          Name des Boards
          <input autoFocus required maxLength={120} value={title} onChange={(e) => setTitle(e.target.value)} placeholder={empty ? 'z. B. Sommerakademie 2027' : t.name} className={fieldClass} />
        </label>
        <TargetFields t={tgt} idPrefix="new-board" />
        {t.cardCount > 0 && (
          <label className="flex min-h-11 items-center gap-3 text-sm">
            <input type="checkbox" checked={withCards} onChange={(e) => setWithCards(e.target.checked)} className="size-5 accent-[var(--color-accent)]" />
            Mit den {plural(t.cardCount, 'Beispielkarte', 'Beispielkarten')}
          </label>
        )}
        {t.setup && withCards && t.cardCount > 0 && (
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            {t.setup.label} (optional)
            <input type="date" value={setupDate} onChange={(e) => setSetupDate(e.target.value)} className={fieldClass} aria-describedby="nb-setup-help" />
            <span id="nb-setup-help" className="text-xs font-normal text-[var(--ink-soft)]">
              {t.setup.help} Ohne Datum bleiben die Karten ohne Frist.
            </span>
          </label>
        )}
        <Button type="submit" variant="primary" disabled={busy || !title.trim()}>
          {busy ? 'Lege an …' : empty ? 'Leeres Board anlegen' : 'Mit dieser Vorlage starten'}
        </Button>
        <p className="text-xs text-[var(--ink-soft)]">
          Spalten, Labels und Regeln kannst du danach jederzeit ändern. Das Board ist zunächst nur für dich sichtbar – freigeben kannst du es im Board.
        </p>
        {!t.builtIn && (t.createdBy === me?.id || me?.isAdmin) && (
          <button
            type="button"
            className="inline-flex min-h-11 w-fit items-center gap-1.5 text-sm text-rose-600 hover:underline"
            onClick={async () => {
              if (!window.confirm(`Vorlage „${t.name}“ löschen?`)) return;
              try {
                await api.del(`/api/templates/${t.id}`);
                toast.info('Vorlage gelöscht.');
                onDeleted();
              } catch (err) {
                toast.error((err as Error).message);
              }
            }}
          >
            <Trash2 size={15} /> Vorlage löschen
          </button>
        )}
      </form>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex flex-col-reverse items-center rounded-lg bg-[var(--column)] px-1 py-2 text-center">
      <dt className="text-[11px] text-[var(--ink-soft)]">{label}</dt>
      <dd className="text-lg font-semibold tabular-nums">{value}</dd>
    </div>
  );
}
